/**
 * Admin API Service
 * Handles communication with backend for admin user management.
 */

import { BaseApiService } from './BaseApiService';
import { API_URL } from '@/lib/apiConfig';

export interface AdminUser {
  userId: string;
  email: string;
  fullName: string | null;
  plan: string | null;
  status: string;
  createdAt: string;
  lastModified: string | null;
  documentCount: number;
  emailVerified: boolean;
}

export interface ListUsersResponse {
  users: AdminUser[];
  total: number;
  paginationToken?: string;
}

export interface DeleteUserResponse {
  message: string;
  userId: string;
  deletedItems: Record<string, number>;
}

class AdminApiService extends BaseApiService {
  constructor() {
    super(API_URL, 'AdminApi');
  }

  /**
   * List all registered users.
   * Requires admin group membership.
   */
  async listUsers(paginationToken?: string): Promise<ListUsersResponse> {
    this.log.debug('Fetching users list');
    const headers = await this.getHeaders();

    const params = new URLSearchParams();
    if (paginationToken) {
      params.append('paginationToken', paginationToken);
    }

    const url = `${this.baseUrl}/admin/users${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await fetch(url, { method: 'GET', headers });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      if (response.status === 403) {
        throw new Error('Access denied. Admin privileges required.');
      }
      this.log.error('Failed to fetch users', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to fetch users');
    }

    const data = await response.json();
    this.log.info('Users fetched', { total: data.total });
    return data;
  }

  /**
   * Delete a user account and all associated data.
   * Requires admin group membership.
   */
  async deleteUser(userId: string): Promise<DeleteUserResponse> {
    this.log.debug('Deleting user account', { userId });
    const headers = await this.getHeaders();

    const response = await fetch(`${this.baseUrl}/admin/users/${userId}`, {
      method: 'DELETE',
      headers,
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      if (response.status === 403) throw new Error('Access denied. Admin privileges required.');
      if (response.status === 404) throw new Error('User not found.');
      this.log.error('Failed to delete user', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to delete user');
    }

    const data = await response.json();
    this.log.info('User deleted', { userId, deletedItems: data.deletedItems });
    return data;
  }

  /**
   * Reset a user's daily recording quota.
   * Requires admin group membership.
   */
  async resetUserQuota(userId: string): Promise<{ message: string; userId: string; quotaResetAt: string }> {
    this.log.debug('Resetting user quota', { userId });
    const headers = await this.getHeaders();

    const response = await fetch(`${this.baseUrl}/admin/users/${userId}/reset-quota`, {
      method: 'POST',
      headers,
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      if (response.status === 403) throw new Error('Access denied. Admin privileges required.');
      this.log.error('Failed to reset user quota', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to reset user quota');
    }

    const data = await response.json();
    this.log.info('User quota reset', { userId });
    return data;
  }

  /**
   * Get details for a specific user.
   * Requires admin group membership.
   */
  async getUser(userId: string): Promise<AdminUser> {
    this.log.debug('Fetching user details', { userId });
    const headers = await this.getHeaders();

    const response = await fetch(`${this.baseUrl}/admin/users/${userId}`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      if (response.status === 403) throw new Error('Access denied. Admin privileges required.');
      if (response.status === 404) throw new Error('User not found.');
      this.log.error('Failed to fetch user', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to fetch user');
    }

    return response.json();
  }
}

// Export singleton instance
export const adminApi = new AdminApiService();
